import React from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
} from "@mui/material";
import { styled } from "@mui/system";
import HeroImage_2 from "../../components/assets/HeroSection/ExperienceTheRealTime.png";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { GetApp as InstallIcon } from "@mui/icons-material";
import { usePWA } from "../../hooks";

/** —— Netflix-ish tokens —— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
  border: "rgba(255,255,255,0.12)",
};

/** —— Buttons (same component names, new styling) —— */
const GradientButton = styled(Button)(({ theme }) => ({
  background: COLORS.red,
  color: "#fff",
  fontWeight: 800,
  fontSize: "1rem",
  padding: "12px 24px",
  borderRadius: 12,
  textTransform: "none",
  letterSpacing: "0.01em",
  boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
  "&:hover": {
    background: COLORS.redHover,
    boxShadow: "0 16px 32px rgba(229,9,20,0.45)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.95rem",
    padding: "10px 18px",
  },
}));

const OutlinedButton = styled(Button)(({ theme }) => ({
  border: `2px solid ${COLORS.red}`,
  color: COLORS.text,
  fontWeight: 700,
  fontSize: "1rem",
  padding: "12px 24px",
  borderRadius: 12,
  textTransform: "none",
  letterSpacing: "0.01em",
  background: "transparent",
  "&:hover": {
    borderColor: COLORS.redHover,
    backgroundColor: "rgba(229,9,20,0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.95rem",
    padding: "10px 18px",
  },
}));

const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleLogin = () => navigate("/sign-in");
  const handleRegister = () => navigate("/register");
  const [isInstalling, setIsInstalling] = useState(false);
  const { isInstallable, isInstalled, install } = usePWA();
   
const handleInstall = async () => {
    setIsInstalling(true);
    try {
      await install();
    } catch (err) {
      console.error("Failed to install app:", err);
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: { xs: 5, md: 6 },
        px: { xs: 2.5, sm: 4, md: 8 },
        // py: { xs: 6, md: 8 },
        minHeight: { md: "80vh" },
        bgcolor: COLORS.bg,
        overflow: "hidden",
        // subtle vignette + glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1200px 600px at 10% 10%, rgba(229,9,20,0.12), transparent 60%), radial-gradient(900px 500px at 90% 20%, rgba(255,255,255,0.06), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* LEFT — copy */}
      <Box
        sx={{
          flex: 1,
          maxWidth: { md: "50%" },
          textAlign: { xs: "center", md: "left" },
          zIndex: 1,
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h3"}
          sx={{
            fontWeight: 900,
            lineHeight: 1.15,
            color: COLORS.text,
            mb: 2.5,
            letterSpacing: "-0.02em",
            textShadow: "0 1px 0 rgba(0,0,0,0.4)",
          }}
        >
         The Real-Time Infrastructure For Global Audience Engagement
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.textMuted,
            lineHeight: 1.7,
            mb: 3,
            fontSize: { xs: "1rem", md: "1.05rem" },
            maxWidth: 680,
            mx: { xs: "auto", md: 0 },
          }}
        >
          StageLink connects you with musicians, experts, creators, and mentors for
          unforgettable real-time experiences. Whether you’re joining public events
          or private sessions, StageLink makes it seamless and personal.
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: COLORS.text,
            mb: 4,
            fontSize: { xs: "0.95rem", md: "1.05rem" },
            letterSpacing: "0.25px",
          }}
        >
          <Box component="span" sx={{ color: COLORS.red }}>●</Box> On-demand &nbsp;{" "}
          <Box component="span" sx={{ color: COLORS.red }}>●</Box> Live &nbsp;{" "}
          <Box component="span" sx={{ color: COLORS.red }}>●</Box> Interactive
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          justifyContent={{ xs: "center", md: "flex-start" }}
        >
          <GradientButton onClick={handleLogin}>Login</GradientButton>
          <OutlinedButton onClick={handleRegister}>Register</OutlinedButton>
           {  isInstallable && !isInstalled && <Button
                            onClick={handleInstall}
                            variant="outlined"
                            disabled={isInstalling}
                            loading={isInstalling}
                            startIcon={<InstallIcon />}
                            sx={{
                              ml: 1,
                              borderColor: COLORS.red,
                              color: COLORS.text,
                              fontWeight: 700,
                              textTransform: "none",
                              px: 2.5,
                              "&:hover": {
                                borderColor: COLORS.redHover,
                                backgroundColor: "rgba(229,9,20,0.08)",
                              },
                            }}
                          >
                            {isInstalling ? "Installing..." : "Install App"}
                          </Button>}
        </Stack>
      </Box>

      {/* RIGHT — visual */}
      <Box
        sx={{
          flex: 1.1,
          maxWidth: { md: "46%" },
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* subtle red glow behind image */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            transform: "translateY(6%)",
            filter: "blur(60px)",
            background:
              "radial-gradient(500px 280px at 70% 60%, rgba(229,9,20,0.28), transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <Box
          component="img"
          src={HeroImage_2}
          alt="Live interaction with talent"
          loading="eager"
          decoding="async"
          sx={{
            width: "100%",
            maxWidth: { xs: 420, sm: 520, md: "100%" },
            borderRadius: 16,
            objectFit: "cover",
            display: "block",
            mx: { xs: "auto", md: 0 },
            boxShadow:
              "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset",
          }}
        />
      </Box>
    </Box>
  );
};

export default HeroSection;
